import * as THREE from 'three';
import DualloMarkerProvenanceClient from "@/clients/duallo/DualloMarkerProvenanceClient";
import DualloFramesClient from "@/clients/duallo/DualloFramesClient";
import DualloCameraHelperClient from "@/clients/duallo/DualloCameraHelperClient";
import organizationSasService from "@/services/OrganizationSasService";

/** Filename without directory or extension — used to pair a provenance record's
 *  source image with the scan's frame records, which may differ in extension
 *  (the visibility file records FlowEngine's source name, frames are stored as
 *  .webp). */
const stemOf = (fileName) =>
    (fileName ?? "").split(/[\\/]/).pop().replace(/\.[^.]+$/, "").toLowerCase();

/**
 * Maps a viewer-frame blob path to its pre-generated inference JPEG — the exact image the
 * model ran on, written by model-gen at frame time (keyframes only):
 *   {assetId}/scans/{scanId}/frames/{stem}.webp → {assetId}/scans/{scanId}/frames/inference/{stem}.jpg
 * Mirrors the inference service's KeyframeTarPackager.InferenceJpegPath. Case is preserved so
 * the signed URL resolves the real (case-sensitive) blob.
 */
const inferenceJpegPath = (imagePath) => {
    const normalized = (imagePath ?? "").replace(/\\/g, "/").replace(/^\/+/, "");
    if (!normalized) return "";
    const slash = normalized.lastIndexOf("/");
    const dir = slash >= 0 ? normalized.slice(0, slash) : "";
    const file = slash >= 0 ? normalized.slice(slash + 1) : normalized;
    const dot = file.lastIndexOf(".");
    const stem = dot > 0 ? file.slice(0, dot) : file;
    return dir ? `${dir}/inference/${stem}.jpg` : `inference/${stem}.jpg`;
};

const distanceBetween = (a, b) => {
    if (!a || !b) return Number.POSITIVE_INFINITY;
    const dx = (a.x ?? 0) - (b.x ?? 0);
    const dy = (a.y ?? 0) - (b.y ?? 0);
    const dz = (a.z ?? 0) - (b.z ?? 0);
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

class MarkerProvenanceService {

    // Per-scan cache: the viewer loads a scan's provenance ONCE (warmed on model open) and every
    // finding selection reuses it instead of re-fetching the whole scan. Caches the promise so
    // concurrent callers dedupe; the mutations below bust the affected scan's entry.
    #byScanId = new Map();   // scanId → Promise<MarkerProvenance[]>

    async getByScanId(scanId, { force = false } = {}) {
        if (!scanId) return [];
        if (!force && this.#byScanId.has(scanId)) return this.#byScanId.get(scanId);
        const pending = DualloMarkerProvenanceClient.getByScanId(scanId)
            .catch((err) => { this.#byScanId.delete(scanId); throw err; });   // let a failed load retry
        this.#byScanId.set(scanId, pending);
        return pending;
    }

    /** Warm the cache for a set of scans up front — called when the model opens. */
    async preload(scanIds = []) {
        await Promise.allSettled([...new Set(scanIds)].filter(Boolean).map(id => this.getByScanId(id)));
    }

    /** Drop cached provenance: one scan, or everything (pass no id, e.g. on leaving an asset). */
    invalidate(scanId = null) {
        if (scanId) this.#byScanId.delete(scanId); else this.#byScanId.clear();
    }

    /** Create one provenance record (manual-marker path). */
    async create(model) {
        const created = await DualloMarkerProvenanceClient.create(model);
        this.invalidate(model?.scanId);
        return created;
    }

    /** Delete one provenance record by id (used to replace a marker's record on re-capture). */
    async deleteById(scanId, id) {
        const result = await DualloMarkerProvenanceClient.deleteById(scanId, id);
        this.invalidate(scanId);
        return result;
    }

    /** Number of provenance records for a scan (cheap check to gate the review entry). */
    async getCount(scanId) {
        return await DualloMarkerProvenanceClient.getCountByScanId(scanId);
    }

    /**
     * Finds the provenance behind one marker, plus the detections that were
     * folded into it as duplicates.
     *
     * Matched on position rather than markerIndex: the index is the marker's
     * order within the batch the inference service POSTed, which is not a
     * durable key. A canonical record's position is exactly what was sent as the
     * marker's transform, so nearest-position is a reliable join.
     */
    async getForMarker(scanId, marker) {
        const all = await this.getByScanId(scanId);
        if (!all.length) return null;

        const canonicals = all.filter(r => !r.isDuplicate);

        // Prefer the durable id link: the inference service stamps provenance.markerId with the
        // created marker's id, so a direct id match is exact. Records written before MarkerId
        // existed have none, so fall back to nearest-position for those.
        const markerId = marker?.id;
        if (markerId) {
            // Re-capture writes a replacement record (and best-effort deletes the old), but if an old
            // one lingers, prefer the NEWEST so the viewer always reflects the latest capture.
            const byId = canonicals
                .filter(r => r.markerId === markerId)
                .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
            if (byId) {
                const duplicates = all.filter(r => r.duplicateOfId === byId.id);
                return { canonical: byId, duplicates, matchDistance: 0 };
            }
        }

        // Position fallback is ONLY for legacy records with no markerId. A record that already carries a
        // markerId belongs to THAT marker — never hand it to a different marker that happens to sit
        // nearby, or a marker with no provenance of its own would show a neighbour's image.
        const markerPosition = marker?.transform?.position;
        let canonical = null;
        let best = Number.POSITIVE_INFINITY;

        for (const record of canonicals) {
            if (record.markerId) continue;
            const distance = distanceBetween(record.position, markerPosition);
            if (distance < best) {
                best = distance;
                canonical = record;
            }
        }

        if (!canonical) return null;

        const duplicates = all.filter(r => r.duplicateOfId === canonical.id);

        return { canonical, duplicates, matchDistance: best };
    }

    /**
     * Signed URLs for the frames a set of provenance records came from, keyed by
     * filename stem. Frames are fetched once per scan and shared across every
     * observation in the dialog.
     */
    async getFrameUrlsByStem(scanId, records) {
        const wanted = new Set(records.map(r => stemOf(r.sourceImageFileName)).filter(Boolean));
        if (!wanted.size) return {};

        const frames = await DualloFramesClient.getFramesByScanId(scanId);
        const urls = {};

        await Promise.all(
            (frames ?? [])
                .filter(frame => wanted.has(stemOf(frame.fileName)))
                .map(async (frame) => {
                    if (!frame.imagePath) return;
                    urls[stemOf(frame.fileName)] = await organizationSasService.getSignedUrl(frame.imagePath);
                })
        );

        return urls;
    }

    /**
     * Signed URL for a record's source image, resolving a cube/mono FRAME or an EQUIRECT panorama.
     * Re-captured 360 markers store the equirect filename as their source (shown as the full image),
     * which isn't in the frames store — fall back to the equirect blob paths for those.
     */
    async getSourceImageUrl(record, { scanId, assetId, orgId }) {
        const name = record?.sourceImageFileName;
        if (!name) return null;

        const urls = await this.getFrameUrlsByStem(scanId, [record]).catch(() => ({}));
        const hit = urls[stemOf(name)];
        if (hit) return hit;

        try {
            const paths = await DualloFramesClient.getEquirectFramePaths(orgId, assetId, scanId);
            const want = name.toLowerCase();
            const match = (paths ?? []).find(p => (p.split(/[\\/]/).pop() || '').toLowerCase() === want);
            if (match) return await organizationSasService.getSignedUrl(match.startsWith('/') ? match : `/${match}`);
        } catch { /* no equirect endpoint */ }
        return null;
    }

    /**
     * Every frame in the scan's inference batch as { stem, fileName, url }, signed for
     * display. Unlike getFrameUrlsByStem (which keeps only frames that produced provenance),
     * this returns the whole batch so the reviewer can also see images the model found
     * nothing on — the candidates for missed detections that need a box adding.
     */
    async getScanFrames(scanId) {
        const frames = await DualloFramesClient.getFramesByScanId(scanId);
        const out = [];

        await Promise.all(
            (frames ?? [])
                .filter(frame => frame.imagePath)
                .map(async (frame) => {
                    // Viewer copy (.webp) for display; the pre-generated inference JPEG for export
                    // (the real image the model saw, and a format Roboflow ingests directly).
                    const infPath = inferenceJpegPath(frame.imagePath);
                    out.push({
                        stem: stemOf(frame.fileName),
                        fileName: frame.fileName,
                        url: await organizationSasService.getSignedUrl(frame.imagePath),
                        inferenceFileName: infPath ? infPath.split("/").pop() : null,
                        inferenceUrl: infPath ? await organizationSasService.getSignedUrl(infPath) : null,
                    });
                })
        );

        return out;
    }

    stemOf(fileName) {
        return stemOf(fileName);
    }

    /** Camera helpers for a scan, keyed by image filename stem. */
    async getCameraHelpersByStem(scanId) {
        const helpers = await DualloCameraHelperClient.getCameraHelpersByScanId(scanId);
        const byStem = {};

        for (const helper of helpers ?? []) {
            const stem = stemOf(helper.imageName);
            if (stem) byStem[stem] = helper;
        }

        return byStem;
    }

    /**
     * Projects a world position back into its source image through the camera
     * helper, returning pixel coordinates.
     *
     * The camera is rebuilt exactly as CameraHelperMarker builds it for the
     * viewer — same Euler order, same degrees-to-radians, same FOV derived from
     * focalY and image height — so this projection agrees with what the 3D scene
     * shows. Where it lands relative to the detection's bounding box is the
     * round-trip check: a marker placed from a bbox should project back inside
     * that same bbox. A consistent miss means the pixel coordinates the
     * inference service matched against were not in the same space as the
     * image the model actually saw.
     */
    projectToImage(helper, worldPosition) {
        if (!helper || !worldPosition) return null;
        if (!helper.imageWidth || !helper.imageHeight || !helper.focalY) return null;

        const camera = new THREE.PerspectiveCamera();

        camera.position.set(
            Number(helper.transform.position.x),
            Number(helper.transform.position.y),
            Number(helper.transform.position.z)
        );

        camera.setRotationFromEuler(new THREE.Euler(
            THREE.MathUtils.degToRad(Number(helper.transform.rotation.x)),
            THREE.MathUtils.degToRad(Number(helper.transform.rotation.y)),
            THREE.MathUtils.degToRad(Number(helper.transform.rotation.z)),
            helper.transform.rotation.order
        ));

        camera.fov = 2 * Math.atan(helper.imageHeight / (2 * helper.focalY)) * (180 / Math.PI);
        camera.aspect = helper.imageWidth / helper.imageHeight;
        camera.near = 0.01;
        camera.far = Math.max(Number(helper.cameraFar) || 0, 1000);

        camera.updateProjectionMatrix();
        camera.updateMatrixWorld(true);

        const point = new THREE.Vector3(
            worldPosition.x ?? 0,
            worldPosition.y ?? 0,
            worldPosition.z ?? 0
        );

        // Depth in camera space: negative Z is in front of the camera in three's
        // convention, so a positive value here means the point is BEHIND it and
        // the projected pixel is meaningless.
        const viewSpace = point.clone().applyMatrix4(camera.matrixWorldInverse);
        const isBehindCamera = viewSpace.z > 0;

        const ndc = point.clone().project(camera);

        // NDC is [-1, 1] with Y up; image pixels run from the top-left with Y down.
        const x = ((ndc.x + 1) / 2) * helper.imageWidth;
        const y = ((1 - ndc.y) / 2) * helper.imageHeight;

        return {
            x,
            y,
            // The same point under the opposite vertical convention. If THIS one
            // lands in the bbox while the one above doesn't, the pixel origin is
            // flipped somewhere in the pipeline.
            flippedY: helper.imageHeight - y,
            isBehindCamera,
            imageWidth: helper.imageWidth,
            imageHeight: helper.imageHeight
        };
    }

    async deleteByScanId(scanId) {
        const result = await DualloMarkerProvenanceClient.deleteByScanId(scanId);
        this.invalidate(scanId);
        return result;
    }

    /**
     * Record a reviewer's verdict (TruePositive / FalsePositive), plus optional relabel, notes,
     * and a corrected bbox ({ x, y, width, height } in image px, centre x/y) for a right-class
     * but mis-localised detection.
     */
    async review(scanId, id, { reviewStatus, correctedLabel = null, reviewNotes = null, correctedBbox = null }) {
        const result = await DualloMarkerProvenanceClient.reviewById(scanId, id, {
            reviewStatus,
            correctedLabel,
            reviewNotes,
            correctedBboxX: correctedBbox ? correctedBbox.x : null,
            correctedBboxY: correctedBbox ? correctedBbox.y : null,
            correctedBboxWidth: correctedBbox ? correctedBbox.width : null,
            correctedBboxHeight: correctedBbox ? correctedBbox.height : null,
        });
        this.invalidate(scanId);
        return result;
    }

    /** Corrected annotation dataset for a scan, ready to push to Roboflow for retraining. */
    async exportDataset(scanId, options = {}) {
        return await DualloMarkerProvenanceClient.exportDataset(scanId, options);
    }
}

export default new MarkerProvenanceService();
